import { createPortal } from 'react-dom';
import { useState } from 'react';
import { Sparkles, Brain, Loader2 } from 'lucide-react';
import { useGenerateEducationContent } from '../../services/query/education/education';
import { showToast } from '../../lib/utils/toast';

interface Props {
  onClose: () => void;
  onSuccess?: () => void;
}


export default function AIContentGeneratorModal({ onClose, onSuccess }: Props) {
  const [form, setForm] = useState({
    content_topic: '',
    content_type: '',
    category: '',
    language: '',
    additional_instructions: '',
  });

  const { mutateAsync: generate, isPending } = useGenerateEducationContent();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async () => {
    if (!form.content_topic.trim()) {
      showToast.error('Content topic is required');
      return;
    }
    try {
      await generate(form);
      showToast.success('Content generated successfully');
      onSuccess?.();
      onClose();
    } catch {
      showToast.error('Failed to generate content');
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[500] p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-green-500 to-green-600 rounded-lg">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">AI Content Generator</h3>
              <p className="text-sm text-gray-600">Create personalized educational content instantly</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg" aria-label="Close">
            ✕
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Content Topic <span className="text-red-500">*</span></label>
            <input
              name="content_topic"
              type="text"
              value={form.content_topic}
              onChange={handleChange}
              placeholder="e.g., Root Canal Treatment Recovery"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Content Type</label>
              <select name="content_type" value={form.content_type} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500">
                <option value="">Select Content Type</option>
                <option value="video_script">Videos</option>
                <option value="article">Articles</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <select name="category" value={form.category} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500">
                <option value="">Select Category</option>
                <option value="general">General</option>
                <option value="high_risk">High Risk</option>
                <option value="post_treatment">Post Treatment</option>
                <option value="procedures">Procedures</option>
                <option value="preventive_care">Preventive Care</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
            <select name="language" value={form.language} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500">
              <option value="">Select Language</option>
              <option value="en">English</option>
              <option value="es">Español</option>
              <option value="fr">Français</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Additional Instructions (Optional)</label>
            <textarea
              name="additional_instructions"
              rows={3}
              value={form.additional_instructions}
              onChange={handleChange}
              placeholder="Add any specific requirements or context..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Brain className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-semibold">AI-Powered Generation</p>
                <p className="text-blue-700 mt-1">
                  Content will be automatically generated based on your specifications and best practices in patient education.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <button onClick={onClose} disabled={isPending} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isPending || !form.content_topic.trim()}
            className="px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
            <span>{isPending ? 'Generating...' : 'Generate Content'}</span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

